const MEDIA_BANK_ENTRY_ID_KEY_PATTERN = /(^|_)media_bank_entry_id$/;

// Keep in sync with AiHtmlMediaReferenceExtractor::isMediaBankEntryReference().
// The unified button keeps a linked file's entry id in target_data rather than a *_media_bank_entry_id key.
const isMediaBankEntryReference = (key, container) => MEDIA_BANK_ENTRY_ID_KEY_PATTERN.test(key)
    || (key === 'target_data' && container?.target_type === 'file');

const parseMfComponentConfig = (element) => {
    try {
        return JSON.parse(element.getAttribute('config') || '{}');
    } catch (e) {
        return {};
    }
};

const normalizeMediaEntryId = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const normalized = Number.parseInt(String(value), 10);

    return Number.isNaN(normalized) ? null : String(normalized);
};

export const collectMediaBankEntryIdsFromValue = (value, ids = new Set()) => {
    if (Array.isArray(value)) {
        value.forEach((item) => collectMediaBankEntryIdsFromValue(item, ids));
        return ids;
    }

    if (!value || typeof value !== 'object') {
        return ids;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
        if (isMediaBankEntryReference(key, value)) {
            const normalizedId = normalizeMediaEntryId(nestedValue);

            if (normalizedId !== null) {
                ids.add(normalizedId);
            }

            return;
        }

        collectMediaBankEntryIdsFromValue(nestedValue, ids);
    });

    return ids;
};

export const extractMediaBankEntryIdsFromHtml = (html = '') => {
    const ids = new Set();
    const regex = /data-media-id="(\d+)"/g;
    let match;

    while ((match = regex.exec(html)) !== null) {
        ids.add(match[1]);
    }

    if (typeof document === 'undefined' || !html) {
        return Array.from(ids);
    }

    const container = document.createElement('div');
    container.innerHTML = html;

    container.querySelectorAll('mf-component').forEach((element) => {
        collectMediaBankEntryIdsFromValue(parseMfComponentConfig(element), ids);
    });

    return Array.from(ids);
};

export const getResolvedMediaEntry = (mediaId, mediaEntryMap = {}) => {
    const normalizedId = normalizeMediaEntryId(mediaId);

    if (normalizedId === null || !mediaEntryMap) {
        return null;
    }

    return mediaEntryMap[normalizedId] || null;
};

export const getResolvedMediaUrl = (mediaId, mediaEntryMap = {}, mediaUrlMap = {}) => {
    const normalizedId = normalizeMediaEntryId(mediaId);

    if (normalizedId === null) {
        return null;
    }

    const entry = getResolvedMediaEntry(normalizedId, mediaEntryMap);

    if (entry?.url) {
        return entry.url;
    }

    return mediaUrlMap?.[normalizedId] || null;
};

export const resolveHtmlMediaUrls = (html = '', mediaEntryMap = {}, mediaUrlMap = {}) => {
    if (!html) {
        return html;
    }

    return html.replace(
        /<img([^>]*?)data-media-id="(\d+)"([^>]*?)src="[^"]*"([^>]*?)>/g,
        (match, before, mediaId, mid, after) => {
            const url = getResolvedMediaUrl(mediaId, mediaEntryMap, mediaUrlMap);

            if (url) {
                return `<img${before}data-media-id="${mediaId}"${mid}src="${url}"${after}>`;
            }

            return match;
        }
    ).replace(
        /<img([^>]*?)src="[^"]*"([^>]*?)data-media-id="(\d+)"([^>]*?)>/g,
        (match, before, mid, mediaId, after) => {
            const url = getResolvedMediaUrl(mediaId, mediaEntryMap, mediaUrlMap);

            if (url) {
                return `<img${before}src="${url}"${mid}data-media-id="${mediaId}"${after}>`;
            }

            return match;
        }
    );
};

export const attachResolvedMediaEntriesToContent = (content, mediaEntryMap = {}) => {
    if (Array.isArray(content)) {
        return content.map((item) => attachResolvedMediaEntriesToContent(item, mediaEntryMap));
    }

    if (!content || typeof content !== 'object') {
        return content;
    }

    const nextContent = {};
    const entries = {};

    Object.entries(content).forEach(([key, value]) => {
        nextContent[key] = attachResolvedMediaEntriesToContent(value, mediaEntryMap);

        if (isMediaBankEntryReference(key, content)) {
            const entry = getResolvedMediaEntry(value, mediaEntryMap);

            if (entry) {
                entries[key] = entry;
            }
        }
    });

    if (Object.keys(entries).length > 0) {
        nextContent._entries = {
            ...(nextContent._entries && typeof nextContent._entries === 'object' ? nextContent._entries : {}),
            ...entries,
        };
    }

    return nextContent;
};

export const extendAppContextWithMediaEntries = (appContext, mediaEntryMap = {}) => {
    if (!appContext) {
        return null;
    }

    const provides = Object.create(appContext.provides ?? null);
    provides.aiPageBuilderMediaEntryMap = mediaEntryMap;

    return {
        ...appContext,
        provides,
    };
};
